import { type UseQueryOptions, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";
import { useApi } from "~hooks/apiHooks/useApi";
import { useGetBuildFixtureNameFunc } from "~hooks/useGetBuildFixtureNameFunc.ts";
import type { TransformedFixtureData } from "~pages/Roster/FixtureRosterListPage/types/transformedFixtureData";
import type { FixtureResponse } from "~types/fixture";
import { transformFixtureResponse } from "../utilities/transformFixtureResponse";

const include = ["entities", "persons", "fixtureProfiles"];

export const getGetFixtureQueryKey = ({
  sport,
  organizationId,
  fixtureId,
  seasonId,
}: {
  sport: string;
  organizationId: string;
  fixtureId: string;
  seasonId: string;
}) => ["fixture", sport, organizationId, fixtureId, seasonId, ...include];

export const useGetFixture = (options: Omit<UseQueryOptions<TransformedFixtureData, unknown>, "queryKey" | "queryFn"> = {}) => {
  const request = useApi();
  const { sport, organizationId, fixtureId, seasonId } = useParams();

  const buildFixtureName = useGetBuildFixtureNameFunc();

  if (!sport || !organizationId || !fixtureId || !seasonId) {
    console.error({ sport, organizationId, fixtureId, seasonId });
    throw new Error("Missing required params");
  }

  return useQuery<TransformedFixtureData, unknown>({
    queryKey: getGetFixtureQueryKey({
      sport,
      organizationId,
      fixtureId,
      seasonId,
    }),
    queryFn: () =>
      request<FixtureResponse>(`${sport}/o/${organizationId}/fixtures/${fixtureId}?include=${include.join(",")}`).then((data) =>
        transformFixtureResponse(data, buildFixtureName),
      ),
    enabled: Boolean(fixtureId && organizationId && sport),
    ...options,
  });
};
